import { Injectable, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { PointTechModePayload } from '../../register-user/interfaces';
import { ToolsService } from 'src/app/services/tools.service';
import { INFORMER_ROUTE_ITEMS_STORAGE_KEY } from 'src/app/interfaces';

@Injectable({
  providedIn: 'root'
})
export class TechItemsRouteService {
  private _tools = inject(ToolsService);

  /**
   * HRS-2990: la selección se persiste con el mismo TTL que el modo del
   * informador. El técnico va añadiendo contenidos mientras navega, y cualquier
   * recarga (o una navegación que recargue la página) le vaciaba la lista.
   * Se limpia al guardar la ruta y al salir del modo.
   */
  private _stored = this._restore();

  // State service signals  
  private _techSelectedPointList = signal<number[]>(this._stored.points);
  public techSelectedPointList = this._techSelectedPointList.asReadonly();

  private _techSelectedEventList = signal<number[]>(this._stored.events);
  public techSelectedEventList = this._techSelectedEventList.asReadonly();
  
  // Computed signals
  public selectedTechItemList: Signal<PointTechModePayload[]> = computed(() => {
    if (this._techSelectedPointList().length === 0 && this._techSelectedEventList().length === 0) return [];
    
    return [
      ...this._techSelectedPointList().map(id => ({ id, type: 'point' })), 
      ...this._techSelectedEventList().map(id => ({ id, type: 'event' }))
    ];
  });
  
  // Methods
  public isTechItemSelected(id: number): boolean {
    return this._techSelectedPointList().includes(id) || this._techSelectedEventList().includes(id);
  }

  public addNewPointToList(id: number): void {
    this._addNewItemToList(this._techSelectedPointList, id);
  }

  public addNewEventToList(id: number): void {
    this._addNewItemToList(this._techSelectedEventList, id);
  }

  /** Se vacía al guardar la ruta y al salir del modo: la siguiente empieza limpia. */
  public clear(): void {
    this._techSelectedPointList.set([]);
    this._techSelectedEventList.set([]);
    this._persist();
  }

  public addItemsToList(items: { id: number; type: 'point' | 'event' }[]): void {
    const pointIds = items.filter(item => item.type === 'point').map(item => item.id);
    const eventIds = items.filter(item => item.type === 'event').map(item => item.id);
    if (pointIds.length) this._techSelectedPointList.update(prev => [...prev, ...pointIds.filter(id => !prev.includes(id))]);
    if (eventIds.length) this._techSelectedEventList.update(prev => [...prev, ...eventIds.filter(id => !prev.includes(id))]);
    this._persist();
  }

  public removeItemsFromList(items: { id: number; type: 'point' | 'event' }[]): void {
    const pointIds = new Set(items.filter(item => item.type === 'point').map(item => item.id));
    const eventIds = new Set(items.filter(item => item.type === 'event').map(item => item.id));
    if (pointIds.size) this._techSelectedPointList.update(prev => prev.filter(id => !pointIds.has(id)));
    if (eventIds.size) this._techSelectedEventList.update(prev => prev.filter(id => !eventIds.has(id)));
    this._persist();
  }

  public areAllSelected(items: { id: number; type: 'point' | 'event' }[]): boolean {
    if (items.length === 0) return false;
    return items.every(item => this.isTechItemSelected(item.id));
  }

  private _addNewItemToList(target: WritableSignal<number[]>, id: number): void {
    target.update(prev => prev.includes(id) ? prev.filter(itemId => itemId !== id) : [...prev, id]);
    this._persist();
  }

  private _persist(): void {
    const points = this._techSelectedPointList();
    const events = this._techSelectedEventList();

    if (points.length === 0 && events.length === 0) {
      localStorage.removeItem(INFORMER_ROUTE_ITEMS_STORAGE_KEY);
      return;
    }

    this._tools.setWithExpiry(INFORMER_ROUTE_ITEMS_STORAGE_KEY, { points, events });
  }

  private _restore(): { points: number[]; events: number[] } {
    const stored = this._tools.getWithExpiry(INFORMER_ROUTE_ITEMS_STORAGE_KEY);
    const ids = (value: unknown): number[] =>
      Array.isArray(value) ? value.filter(id => Number.isInteger(id)) : [];

    return { points: ids(stored?.points), events: ids(stored?.events) };
  }
}
